import { useState, useEffect, useRef } from "react";
import { useAppDispatch } from "../store";
import { getValidSasToken, fetchSasToken } from "../store/documentsSlice";
import styled from "styled-components";
import { Button, Typography, Spin } from "antd";
import axios from "axios";
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';


type Props = {
  src: string;
  envConfig: any;
  onPageCount?: (pageCount: number | null) => void;
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  position: relative;
  background: #303030;
`;

const Embed = styled.embed`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: contain;
  position: absolute;
  background: #303030;
`;

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  position: relative;
  background: #303030;
  z-index: 9999;
`;

const DownloadContainer = styled.div`
  width: 200px;
  text-align: center;
  align-self: center;
`;

const unsupportedExtensions = [".doc", ".docx", ".xls", ".xlsx", ".csv", ".mov", ".mp4"];

const axiosWithoutAuth = axios.create();

export default function DocumentRenderer({ src, envConfig, onPageCount }: Props) {
  const normalizedSrc = src ? src.toLowerCase() : src;

  const [isLoading, setIsLoading] = useState(true);
  const [embedSrc, setEmbedSrc] = useState<string | null>(null); // State to manage the embed source
  const [, setPageCount] = useState<number | null>(null);
  const embedSrcRef = useRef<string | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const encodeLink = (link: string, sasToken: string) => {
      return `https://${envConfig.appConfig.storageAccountName}.blob.core.windows.net/dropzonehistory/` + encodeURIComponent(link) + `?` + sasToken;
    };

    const handlePdfPageCount = async (blobUrl: string) => {
      if (normalizedSrc.endsWith(".pdf") && typeof onPageCount === "function") {
        const loadingTask = pdfjs.getDocument(blobUrl);
        const pdf = await loadingTask.promise;
        console.log("Document loaded successfully with", pdf.numPages, "pages.");
        setPageCount(pdf.numPages);
        onPageCount(pdf.numPages);
      } else {
        setPageCount(null);
        if (typeof onPageCount === "function") onPageCount(null);
      }
    };

    const fetchAndSetEmbedSrc = async () => {
      if (!src) return;

      try {
        // Ensure the SAS token is valid
        const validSasToken = await dispatch(getValidSasToken()).unwrap();
        const encodedSrc = encodeLink(src, validSasToken);

        // Fetch the resource and create a Blob URL
        const response = await axiosWithoutAuth.get(encodedSrc, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(response.data);

        setEmbedSrc(blobUrl);
        embedSrcRef.current = blobUrl;

        await handlePdfPageCount(blobUrl);
      } catch (error) {
        // Refresh the SAS token
        const controller = new AbortController();
        const refreshedSasToken = await dispatch(fetchSasToken({ controller })).unwrap();
        controller.abort();

        // Retry with the new token
        const refreshedEncodedSrc = encodeLink(src, refreshedSasToken);
        try {
          const refreshedResponse = await axiosWithoutAuth.get(refreshedEncodedSrc, { responseType: "blob" });
          const refreshedBlobUrl = URL.createObjectURL(refreshedResponse.data);

          setEmbedSrc(refreshedBlobUrl);
          embedSrcRef.current = refreshedBlobUrl;

          await handlePdfPageCount(refreshedBlobUrl);
        } catch (retryError) {
          console.error("Failed to fetch and set embed source after retry:", retryError);
          setEmbedSrc(null);
          embedSrcRef.current = null;
          setPageCount(null);
        }
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchAndSetEmbedSrc();

    // Cleanup Blob URL when the component unmounts or embedSrc changes
    return () => {
      if (embedSrcRef.current) {
        URL.revokeObjectURL(embedSrcRef.current);
        embedSrcRef.current = null;
      }
    };
  }, [envConfig.appConfig.storageAccountName, src, normalizedSrc, dispatch, onPageCount]);

  if (!embedSrc && !isLoading) {
    return (
      <Container>
        <Typography.Text strong italic style={{ color: "white" }}>
          Failed to load the document. Please reload the page or try again later.
        </Typography.Text>
      </Container>
    );
  }

  if (embedSrc && normalizedSrc && unsupportedExtensions.some((extension) => normalizedSrc.endsWith(extension))) {
    return (
      <Container>
        <DownloadContainer>
          <Typography.Text strong italic style={{ color: "white" }}>
            Unsupported document type.
          </Typography.Text>

          <Button
            style={{ width: "100%", marginTop: 10 }}
            onClick={() => {
              const iframe = document.createElement("iframe");
              iframe.style.display = "none";
              iframe.src = embedSrc;
              document.body.appendChild(iframe);
              iframe.onload = () => {
                document.body.removeChild(iframe);
              };
            }}
          >
            Download document
          </Button>
        </DownloadContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Embed
        onError={() => {
          console.error("Failed to load embed source.");
          setEmbedSrc(null);
          embedSrcRef.current = null;
          setIsLoading(false);
        }}
        src={
          embedSrc ??
          (normalizedSrc?.endsWith(".pdf") ? "" : "data:image/gif;base64,R0lGODlhAQABAIAAAP///AMDAwAAACwAAAAAAQABAAACAkQBADs=")
        }
      />
      {isLoading && (
        <Overlay>
          <Spin size="large" className="white-spin" />
        </Overlay>
      )}
    </Container>
  );
}
